import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Loader2, ChevronRight, X } from "lucide-react";

const FILTERS = [["all", "All"], ["prospects", "Prospects"], ["customers", "Customers"]];

export default function MobileCustomers() {
  const navigate = useNavigate();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [limit, setLimit] = useState(60);

  useEffect(() => {
    api.get("/crm/customers").then(({ data }) => setAll(data.customers))
      .catch(() => toast.error("Could not load customers")).finally(() => setLoading(false));
  }, []);

  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return all
      .filter((c) => (filter === "all" ? true : filter === "prospects" ? c.is_prospect : !c.is_prospect))
      .filter((c) => !t || [c.company_name, c.contact_name, c.city, c.account_number, c.phone].some((v) => (v || "").toLowerCase().includes(t)))
      .sort((a, b) => (a.company_name || "").localeCompare(b.company_name || ""));
  }, [all, q, filter]);

  return (
    <div data-testid="m-customers">
      <div className="sticky top-[var(--m-header-h,60px)] z-20 bg-[#F4F4F5] px-4 pt-4 pb-3 space-y-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setLimit(60); }} placeholder="Search company, contact, city…" data-testid="m-cust-search"
            className="w-full h-11 pl-9 pr-9 bg-white border border-zinc-200 text-base outline-none focus:border-[#D4AF37]" />
          {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400" aria-label="Clear"><X className="h-4 w-4" /></button>}
        </div>
        <div className="flex gap-2">
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => { setFilter(k); setLimit(60); }} data-testid={`m-cust-filter-${k}`}
              className={`px-3 py-1.5 text-xs font-semibold border ${filter === k ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-zinc-600 border-zinc-300"}`}>{l}</button>
          ))}
          <span className="ml-auto self-center text-xs text-zinc-500" data-testid="m-cust-count">{list.length}</span>
        </div>
      </div>
      {loading ? <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div> : (
        <div className="px-4 space-y-2">
          {list.slice(0, limit).map((c) => (
            <button key={c.id} onClick={() => navigate(`/mobilecrm/customers/${c.id}`)} data-testid={`m-cust-${c.id}`}
              className={`w-full text-left bg-white border border-zinc-200 border-l-4 p-3 flex items-center gap-3 active:bg-zinc-50 ${c.is_prospect ? "border-l-[#3B82F6]" : "border-l-[#D4AF37]"}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#0F172A] truncate">{c.company_name || "Unnamed"}</div>
                <div className="text-xs text-zinc-500 truncate">{[c.contact_name, c.city && `${c.city}${c.state ? `, ${c.state}` : ""}`].filter(Boolean).join(" · ") || "No details yet"}</div>
              </div>
              <ChevronRight className="h-4 w-4 text-zinc-300 shrink-0" />
            </button>
          ))}
          {list.length > limit && <button onClick={() => setLimit((l) => l + 60)} data-testid="m-cust-more" className="w-full py-3 text-sm text-[#0F172A] border border-zinc-300 bg-white">Show more</button>}
          {list.length === 0 && <p className="py-10 text-center text-sm text-zinc-500" data-testid="m-cust-empty">No matches.</p>}
        </div>
      )}
    </div>
  );
}
